<template>
    <div class="min-h-screen bg-gradient-to-b from-nomad-900 to-black">
        <!-- Hero Section -->
        <div class="relative">
            <!-- Background Image with Overlay -->
            <div class="absolute inset-0 h-96">
                <img :src="audiobook.cover_image" :alt="audiobook.title"
                    class="w-full h-full object-cover opacity-20 blur-sm" />
                <div class="absolute inset-0 bg-gradient-to-b from-transparent to-black"></div>
            </div>

            <!-- Content -->
            <div class="relative container mx-auto px-4 pt-20 pb-8">
                <div class="flex flex-col md:flex-row gap-8 items-start">
                    <!-- Cover Image -->
                    <div class="flex-shrink-0">
                        <img :src="audiobook.cover_image" :alt="audiobook.title"
                            class="w-64 h-64 object-cover rounded-lg shadow-2xl" />
                    </div>

                    <!-- Info -->
                    <div class="flex-1 text-white">
                        <div v-if="audiobook.is_premium"
                            class="inline-block bg-nomad-accent text-nomad-900 text-xs font-bold px-3 py-1 rounded-full mb-3">
                            PREMIUM
                        </div>

                        <h1 class="text-4xl md:text-5xl font-bold mb-4">{{ audiobook.title }}</h1>

                        <div class="flex flex-wrap gap-6 text-lg mb-6">
                            <div v-if="audiobook.author">
                                <span class="text-nomad-400">By</span>
                                <Link :href="`/artist/${audiobook.author.id}`"
                                    class="font-semibold ml-2 hover:text-nomad-accent transition-colors">
                                {{ audiobook.author.name }}
                                </Link>
                            </div>
                            <div v-if="audiobook.narrator">
                                <span class="text-nomad-400">Narrated by</span>
                                <Link :href="`/artist/${audiobook.narrator.id}`"
                                    class="font-semibold ml-2 hover:text-nomad-accent transition-colors">
                                {{ audiobook.narrator.name }}
                                </Link>
                            </div>
                        </div>

                        <p v-if="audiobook.description" class="text-nomad-300 text-lg leading-relaxed mb-6 max-w-3xl">
                            {{ audiobook.description }}
                        </p>

                        <div class="flex flex-wrap gap-4 items-center">
                            <span class="text-nomad-400">{{ audiobook.chapters?.length || 0 }} Chapters</span>
                            <span class="text-nomad-400">•</span>
                            <span class="text-nomad-400">{{ audiobook.language === 'so' ? 'Somali' : audiobook.language
                                === 'en' ? 'English' : 'Arabic' }}</span>
                            <span class="text-nomad-400" v-if="audiobook.play_count">•</span>
                            <span class="text-nomad-400" v-if="audiobook.play_count">{{ audiobook.play_count }}
                                plays</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Chapters Section -->
        <div class="container mx-auto px-4 py-12">
            <h2 class="text-2xl font-bold text-white mb-6">Chapters</h2>

            <div v-if="audiobook.chapters && audiobook.chapters.length > 0" class="space-y-2">
                <div v-for="chapter in audiobook.chapters" :key="chapter.id"
                    class="bg-nomad-900 rounded-lg p-4 flex items-center gap-4">

                    <!-- Chapter Info -->
                    <div class="flex-1 min-w-0">
                        <div class="flex items-center gap-3 mb-1">
                            <span class="text-nomad-400 font-semibold">{{ chapter.number }}</span>
                            <h3 class="text-white font-medium truncate">
                                {{ chapter.title || `Chapter ${chapter.number}` }}
                            </h3>
                            <span v-if="chapter.is_preview" class="text-nomad-accent text-xs font-bold">FREE
                                PREVIEW</span>
                        </div>
                        <div class="text-nomad-400 text-sm">
                            {{ formatDuration(chapter.duration) }}
                        </div>
                    </div>

                    <!-- Available on App Badge -->
                    <div class="flex-shrink-0">
                        <span class="text-nomad-500 text-xs bg-nomad-800 px-3 py-1 rounded-full">
                            Available on App
                        </span>
                    </div>
                </div>
            </div>

            <div v-else class="text-center py-12">
                <p class="text-nomad-400 text-lg">No chapters available yet.</p>
            </div>


        </div>
    </div>
</template>

<script setup>
import { usePage, Link } from '@inertiajs/vue3';

const { audiobook } = usePage().props;

function formatDuration(seconds) {
    if (!seconds) return 'Unknown';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
        return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
}
</script>